export const oidcSettings = {
  responseType: "id_token token",
  scope: "openid profile",
  automaticSilentRenew: true,
  automaticSilentSignin: false,
};

export enum EnvType {
  local = "LOCAL",
  localci = "LOCALCI",
  localqa = "LOCALQA",
  localuat = "LOCALUAT",
  ci = "CI",
  qa = "QA",
  uat = "UAT",
  prod = "PROD",
}

export interface OidcSettings {
  env: EnvType;
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri?: string;
  silentRedirectUri?: string;
  googleAnalyticsId: string;
}

export interface OidcEnvironments {
  local: OidcSettings;
  localci: OidcSettings;
  localqa: OidcSettings;
  localuat: OidcSettings;
  ci: OidcSettings;
  qa: OidcSettings;
  uat: OidcSettings;
  prod: OidcSettings;
}

export const oidcEnvironments: OidcEnvironments = {
  local: {
    env: EnvType.local,
    authority: "https://localhost:44351",
    clientId: "BorrowerApplication",
    redirectUri: "https://localhost:44341/oidc-callback",
    silentRedirectUri: "https://localhost:44341/application",
    postLogoutRedirectUri: "https://localhost:44341/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  localci: {
    env: EnvType.localci,
    authority: "https://identity-dev.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://localhost:44340/oidc-callback",
    silentRedirectUri: "https://localhost:44340/application",
    postLogoutRedirectUri: "https://localhost:44340/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  localqa: {
    env: EnvType.localqa,
    authority: "https://identity-qa.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://localhost:44339/oidc-callback",
    silentRedirectUri: "https://localhost:44339/application",
    postLogoutRedirectUri: "https://localhost:44339/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  localuat: {
    env: EnvType.localuat,
    authority: "https://identity-uat.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://localhost:44338/oidc-callback",
    silentRedirectUri: "https://localhost:44338/application",
    postLogoutRedirectUri: "https://localhost:44338/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  ci: {
    env: EnvType.ci,
    authority: "https://identity-dev.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://app-ci-lz02-bwrapp-eus.azurewebsites.net/oidc-callback",
    silentRedirectUri: "https://app-ci-lz02-bwrapp-eus.azurewebsites.net/application",
    postLogoutRedirectUri: "https://app-ci-lz02-bwrapp-eus.azurewebsites.net/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  qa: {
    env: EnvType.qa,
    authority: "https://identity-qa.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://app-lz02-qa-bwrapp-eus.azurewebsites.net/oidc-callback",
    silentRedirectUri: "https://app-lz02-qa-bwrapp-eus.azurewebsites.net/application",
    postLogoutRedirectUri: "https://app-lz02-qa-bwrapp-eus.azurewebsites.net/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_PROD,
  },
  uat: {
    env: EnvType.uat,
    authority: "https://identity-uat.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://apply.uat.studentchoice.org/oidc-callback",
    silentRedirectUri: "https://apply.uat.studentchoice.org/application",
    postLogoutRedirectUri: "https://apply.uat.studentchoice.org/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_DEV,
  },
  prod: {
    env: EnvType.prod,
    authority: "https://identity.studentchoice.org",
    clientId: "borrowerapp",
    redirectUri: "https://apply.studentchoice.org/oidc-callback",
    silentRedirectUri: "https://apply.studentchoice.org/application",
    postLogoutRedirectUri: "https://apply.studentchoice.org/application",
    googleAnalyticsId: process.env.VUE_APP_GOOGLE_ANALYTICS_ID_PROD,
  },
};

export function getEnvironment(): OidcSettings {
  // default to PROD
  let environment = oidcEnvironments.prod;
  const host = window.location.hostname;
  const port = window.location.port;

  if (host.includes("localhost") && port === "44341") {
    environment = oidcEnvironments.local;
  } else if (host.includes("localhost") && port === "44340") {
    environment = oidcEnvironments.localci;
  } else if (host.includes("localhost") && port === "44339") {
    environment = oidcEnvironments.localqa;
  } else if (host.includes("localhost") && port === "44338") {
    environment = oidcEnvironments.localuat;
  } else if (host.includes("-ci")) {
    environment = oidcEnvironments.ci;
  } else if (host.includes("-qa")) {
    environment = oidcEnvironments.qa;
  } else if (host.includes("-uat") || host.includes(".uat.")) {
    environment = oidcEnvironments.uat;
  }

  // disable logging for UAT and PROD environments
  if (environment.env === EnvType.uat || environment.env === EnvType.prod) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    console.log = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    console.warn = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    console.debug = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    console.info = () => {};
  }

  return environment;
}
